/**
 * Public single-article page — `/blog/:slug`.
 *
 * Uses the same PageShell chrome + Site Styles as the rest of the site so
 * the article looks native. Renders the shareable article HTML + a share
 * bar identical to the one inside the module's modal.
 */
import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import axios from "axios";
import PageShell from "@/components/PageShell";
import { getApiUrl } from "@/utils/api";
import { Clock, Share2, ArrowLeft, Loader2 } from "lucide-react";

const API = getApiUrl();

function formatWhen(iso) {
  if (!iso) return "";
  try {
    return new Date(iso).toLocaleString("en-GB", { dateStyle: "medium", timeStyle: "short" });
  } catch {
    return iso;
  }
}

export default function BlogArticlePage() {
  const { slug } = useParams();
  const [article, setArticle] = useState(null);
  const [status, setStatus] = useState("loading");

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await axios.get(`${API}/property-articles/by-slug/${slug}`);
        if (cancelled) return;
        setArticle(data);
        setStatus("ready");
        // Refresh document title so social shares (client-side) get the right title
        if (typeof document !== "undefined" && data?.title) {
          document.title = data.title;
        }
      } catch {
        if (!cancelled) setStatus("not-found");
      }
    })();
    return () => { cancelled = true; };
  }, [slug]);

  const headingStyle = {
    fontFamily: "var(--font-heading, inherit)",
    color: "var(--heading-color, inherit)",
  };
  const bodyStyle = {
    fontFamily: "var(--font-body, inherit)",
    color: "var(--body-color, inherit)",
  };
  const proseStyle = {
    ...bodyStyle,
    "--tw-prose-headings": "var(--heading-color, inherit)",
    "--tw-prose-links": "var(--primary-color, var(--tw-prose-links))",
    "--tw-prose-body": "var(--body-color, var(--tw-prose-body))",
  };
  // Article content already contains its own <h1>title</h1> as the first
  // element (per the AI generation prompt) — size/weight it via Site
  // Styles vars so it matches every other heading on the site. `!important`
  // + later-in-DOM insertion beats Tailwind Typography's fixed prose scale.
  const proseFontOverride = `
    .blog-article-prose h1, .blog-article-prose h2, .blog-article-prose h3,
    .blog-article-prose h4, .blog-article-prose h5, .blog-article-prose h6 {
      font-family: var(--font-heading, inherit);
      color: var(--heading-color, inherit);
      font-weight: var(--heading-weight, 700) !important;
      line-height: var(--heading-line-height, 1.2) !important;
    }
    .blog-article-prose h1 { font-size: var(--h1-size, 48px) !important; }
    .blog-article-prose h2 { font-size: var(--h2-size, 36px) !important; }
    .blog-article-prose h3 { font-size: var(--h3-size, 24px) !important; }
    .blog-article-prose h4 { font-size: var(--h4-size, 20px) !important; }
    .blog-article-prose h5 { font-size: var(--h5-size, 18px) !important; }
    .blog-article-prose h6 { font-size: var(--h6-size, 16px) !important; }
    .blog-article-prose p, .blog-article-prose li, .blog-article-prose figcaption,
    .blog-article-prose strong, .blog-article-prose em {
      font-family: var(--font-body, inherit);
    }
  `;

  const shareUrl = typeof window !== "undefined" ? window.location.href : "";

  return (
    <PageShell chrome={true}>
      <div className="max-w-3xl mx-auto px-4 py-12" data-testid="blog-article-page">
        {status === "loading" && (
          <div className="text-center text-sm text-muted-foreground py-16">
            <Loader2 className="h-4 w-4 animate-spin inline mr-2" /> Loading article…
          </div>
        )}
        {status === "not-found" && (
          <div className="text-center py-16">
            <h1 className="text-2xl mb-2" style={headingStyle}>Article not found</h1>
            <p className="text-muted-foreground text-sm mb-6" style={bodyStyle}>
              This article may have been removed. Head back and try another one.
            </p>
            <Link to="/" className="inline-flex items-center gap-1 text-primary hover:underline">
              <ArrowLeft className="h-4 w-4" /> Back home
            </Link>
          </div>
        )}
        {article && (
          <article data-testid="blog-article-content" data-article-slug={article.slug}>
            <Link to="/" className="inline-flex items-center gap-1 text-xs text-muted-foreground hover:text-primary mb-6">
              <ArrowLeft className="h-3 w-3" /> Back
            </Link>
            {article.cover_image && (
              <img
                src={article.cover_image}
                alt={article.title}
                className="w-full aspect-[16/9] object-cover rounded-md mb-6"
                data-testid="blog-cover-image"
              />
            )}
            <p className="text-xs text-muted-foreground flex items-center gap-1 mb-2">
              <Clock className="h-3 w-3" /> {formatWhen(article.generated_at)}
            </p>
            {/* No separate <h1> here — content_html already opens with its
                own <h1>{title}</h1> (see AI generation prompt), so rendering
                the title again here would duplicate it. */}
            <style>{proseFontOverride}</style>
            <div
              className="blog-article-prose prose max-w-none prose-img:rounded-md prose-img:my-6 prose-img:w-full prose-figcaption:text-center"
              style={proseStyle}
              dangerouslySetInnerHTML={{ __html: article.content_html }}
            />
            <div className="flex items-center gap-2 mt-8 pt-4 border-t border-border" data-testid="blog-share-bar">
              <span className="text-xs text-muted-foreground inline-flex items-center gap-1">
                <Share2 className="h-3.5 w-3.5" /> Share
              </span>
              <a
                href={`https://api.whatsapp.com/send?text=${encodeURIComponent(article.title)}%20${encodeURIComponent(shareUrl)}`}
                target="_blank" rel="noopener noreferrer"
                className="text-xs px-2.5 py-1 rounded border border-border hover:bg-[#25D366]/10 hover:border-[#25D366]/50 text-[#25D366] font-medium"
              >WhatsApp</a>
              <a
                href={`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(shareUrl)}`}
                target="_blank" rel="noopener noreferrer"
                className="text-xs px-2.5 py-1 rounded border border-border hover:bg-[#1877F2]/10 hover:border-[#1877F2]/50 text-[#1877F2] font-medium"
              >Facebook</a>
              <a
                href={`https://twitter.com/intent/tweet?url=${encodeURIComponent(shareUrl)}&text=${encodeURIComponent(article.title)}`}
                target="_blank" rel="noopener noreferrer"
                className="text-xs px-2.5 py-1 rounded border border-border hover:bg-black/5 font-medium"
              >Twitter / X</a>
            </div>
          </article>
        )}
      </div>
    </PageShell>
  );
}
